import { useEffect, useRef } from "react";

/**
 * Cloudflare Turnstile widget — bot protection for the create-account form.
 *
 * Ported from form720-ui/src/components/auth/TurnstileWidget.jsx. The two products
 * share no code, so this is a deliberate copy — keep them in step.
 *
 * Env-gated: renders nothing (and signup proceeds unchanged) unless
 * REACT_APP_TURNSTILE_SITE_KEY is set, so this is inert until a key is provisioned.
 * Once set it loads the Turnstile script, renders the challenge, and reports the
 * token via onToken(token) — and onToken("") on expiry or error, so the caller can
 * clear a stale token rather than submit one Cloudflare will refuse.
 *
 * The token alone is NOT protection: form2290-api's TurnstileService must verify it
 * against Cloudflare's /siteverify, which is what a bot POSTing the endpoint directly
 * cannot satisfy.
 *
 * ⚠️ 2290 needs its OWN widget — Turnstile site keys are bound to hostnames, so the
 * 720 key will not validate on easyfile2290.com. Create one at
 * https://dash.cloudflare.com → Turnstile for the 2290 hostnames, then set:
 *   .env.uat         REACT_APP_TURNSTILE_SITE_KEY=0x4AAAAA...
 *   .env.production  REACT_APP_TURNSTILE_SITE_KEY=0x4AAAAA...
 * and the paired secret as TURNSTILE_SECRET on the server (systemd-creds).
 */
export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
export const isCaptchaEnabled = () => !!TURNSTILE_SITE_KEY;

const SCRIPT_ID = "cf-turnstile-script";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

export default function TurnstileWidget({ onToken, className = "", resetSignal = 0 }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  // Keep the latest callback without re-running the effect.
  const cbRef = useRef(onToken);
  cbRef.current = onToken;

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;
    let cancelled = false;
    let poll = null;

    const render = () => {
      if (cancelled || widgetIdRef.current !== null) return;
      if (!window.turnstile || !containerRef.current) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => cbRef.current && cbRef.current(token),
        "expired-callback": () => cbRef.current && cbRef.current(""),
        "error-callback": () => cbRef.current && cbRef.current(""),
      });
    };

    if (window.turnstile) {
      render();
    } else if (!document.getElementById(SCRIPT_ID)) {
      const s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.onload = render;
      document.head.appendChild(s);
    } else {
      poll = setInterval(() => {
        if (window.turnstile) {
          clearInterval(poll);
          poll = null;
          render();
        }
      }, 200);
    }

    return () => {
      cancelled = true;
      if (poll) clearInterval(poll);
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* noop */
        }
        widgetIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A Turnstile token is single-use: once the server has rejected a submission the token
  // is spent, so without an explicit reset the widget keeps handing back a value
  // /siteverify will refuse as "timeout-or-duplicate" and the user is stuck behind a
  // permanently disabled button. Callers bump resetSignal after a failed submit to arm a
  // fresh challenge.
  useEffect(() => {
    if (!resetSignal) return;
    if (widgetIdRef.current === null || !window.turnstile) return;
    try {
      window.turnstile.reset(widgetIdRef.current);
    } catch {
      /* noop */
    }
  }, [resetSignal]);

  if (!TURNSTILE_SITE_KEY) return null;
  return <div ref={containerRef} className={className} />;
}
